import type { FeatureCollection } from "geojson";
import type { GeoJSONSource, Map } from "mapbox-gl";
import type { GeoJsonFeature, AssignmentFeatureProperties } from "../api/map/types";
import { ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID } from "./adminAssignmentMapHighlight";

export const ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID = "admin-assignments-overview";
export const ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID = "admin-assignments-overview-fill";
export const ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID = "admin-assignments-overview-line";
/** Separate source from the fills: labels also cover the highlighted assignment, which the fills omit. */
export const ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID = "admin-assignments-labels";
export const ADMIN_ASSIGNMENTS_LABELS_LAYER_ID = "admin-assignments-labels-symbol";

/** Blue tones read clearly on neutral OSM greys without competing with the green “Visa” highlight. */
const OVERVIEW_FILL_COLOR = "#2563eb";
const OVERVIEW_FILL_OPACITY = 0.16;
const OVERVIEW_LINE_COLOR = "#1d4ed8";
const OVERVIEW_LINE_WIDTH = 1.5;
const OVERVIEW_LINE_OPACITY = 0.62;

const applyOverviewPaint = (map: Map): void => {
    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        return;
    }
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "fill-color", OVERVIEW_FILL_COLOR);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "fill-opacity", OVERVIEW_FILL_OPACITY);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-color", OVERVIEW_LINE_COLOR);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-width", OVERVIEW_LINE_WIDTH);
    map.setPaintProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "line-opacity", OVERVIEW_LINE_OPACITY);
};

const getBeforeLayerId = (map: Map): string | undefined =>
    map.getStyle()?.layers?.find((l) => l.type === "symbol")?.id;

const ensureOverviewLayers = (map: Map): void => {
    const beforeId = getBeforeLayerId(map);

    if (!map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID)) {
        map.addSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        map.addLayer({
            id: ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID,
            type: "fill",
            source: ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID,
            paint: {
                "fill-color": OVERVIEW_FILL_COLOR,
                "fill-opacity": OVERVIEW_FILL_OPACITY
            }
        }, beforeId);
    }

    if (!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
        map.addLayer({
            id: ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID,
            type: "line",
            source: ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID,
            paint: {
                "line-color": OVERVIEW_LINE_COLOR,
                "line-width": OVERVIEW_LINE_WIDTH,
                "line-opacity": OVERVIEW_LINE_OPACITY
            }
        }, beforeId);
    }
};

const stackOverviewBelowHighlight = (map: Map): void => {
    if (!map.getLayer(ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID)) {
        return;
    }
    if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
        map.moveLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
    }
    if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
        map.moveLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, ADMIN_ASSIGNMENT_HIGHLIGHT_FILL_LAYER_ID);
    }
};

/**
 * Faint assignment polygons on admin basemap. Hidden while drawing so TerraDraw is unobstructed.
 * Callers are responsible for waiting until the style is ready (see runWhenStyleReady).
 */
export function updateAdminAssignmentsOverviewLayer(
    map: Map,
    features: GeoJsonFeature<AssignmentFeatureProperties>[],
    options: { show: boolean }
): void {
    const { show } = options;

    try {
        const hasLayers =
            !!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID) &&
            !!map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID);

        if (!show) {
            if (hasLayers) {
                map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "visibility", "none");
                map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "visibility", "none");
            }
            return;
        }

        // Keep the (empty) layers around instead of removing them — a later data arrival then
        // only needs setData, with no add/remove churn against a style that may be reloading.
        ensureOverviewLayers(map);
        applyOverviewPaint(map);

        const source = map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        const data: FeatureCollection = {
            type: "FeatureCollection",
            features: features as FeatureCollection["features"]
        };
        source.setData(data);

        map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID, "visibility", "visible");
        map.setLayoutProperty(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID, "visibility", "visible");

        stackOverviewBelowHighlight(map);
    } catch (e) {
        console.warn("[MapView] Admin assignments overview update failed:", e);
    }
}

const ensureLabelLayer = (map: Map): void => {
    if (!map.getSource(ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID)) {
        map.addSource(ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID, {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] }
        });
    }

    // No beforeId: labels stay on top of both the faint fills and the green highlight.
    if (!map.getLayer(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID)) {
        map.addLayer({
            id: ADMIN_ASSIGNMENTS_LABELS_LAYER_ID,
            type: "symbol",
            source: ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID,
            layout: {
                "text-field": ["get", "name"],
                "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                "text-size": 12,
                "text-anchor": "center",
                "text-allow-overlap": false,
                "text-padding": 4
            },
            paint: {
                "text-color": "#1e3a8a",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2
            }
        });
    }
};

/**
 * Assignment names rendered at the centre of each polygon on the admin basemap.
 * Kept separate from the fills so the highlighted assignment keeps its label.
 */
export function updateAdminAssignmentLabelsLayer(
    map: Map,
    features: GeoJsonFeature<AssignmentFeatureProperties>[],
    options: { show: boolean }
): void {
    try {
        if (!options.show) {
            if (map.getLayer(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID)) {
                map.setLayoutProperty(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID, "visibility", "none");
            }
            return;
        }

        ensureLabelLayer(map);

        const source = map.getSource(ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        const data: FeatureCollection = {
            type: "FeatureCollection",
            features: features as FeatureCollection["features"]
        };
        source.setData(data);

        map.setLayoutProperty(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID, "visibility", "visible");
    } catch (e) {
        console.warn("[MapView] Admin assignment labels update failed:", e);
    }
}

export function removeAdminAssignmentsOverviewFromMap(map: Map): void {
    try {
        if (map.getLayer(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENTS_LABELS_LAYER_ID);
        }
        if (map.getSource(ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID)) {
            map.removeSource(ADMIN_ASSIGNMENTS_LABELS_SOURCE_ID);
        }
        if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENTS_OVERVIEW_FILL_LAYER_ID);
        }
        if (map.getLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID)) {
            map.removeLayer(ADMIN_ASSIGNMENTS_OVERVIEW_LINE_LAYER_ID);
        }
        if (map.getSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID)) {
            map.removeSource(ADMIN_ASSIGNMENTS_OVERVIEW_SOURCE_ID);
        }
    } catch {
        // style reload race
    }
}
